import type { VideoPage } from './videoDto';

/** Recherche ouverte côté serveur : son identifiant et sa première page. */
export interface SearchStart extends VideoPage {
	/** Identifiant à fournir pour lire les pages suivantes et fermer la recherche. */
	id: string;
}

/**
 * Extrait le message d'erreur d'une réponse HTTP en échec, avec un message
 * de repli si le corps n'est pas un JSON exploitable.
 *
 * @param response - Réponse HTTP dont le statut n'est pas OK.
 * @param fallback - Message à utiliser si le corps ne fournit pas d'erreur.
 * @returns Le message d'erreur à afficher.
 */
async function readErrorMessage(response: Response, fallback: string): Promise<string> {
	try {
		const body = (await response.json()) as { error?: string };
		return body.error ?? fallback;
	} catch {
		return fallback;
	}
}

/**
 * Ouvre une recherche YouTube via `POST /api/search` et retourne sa première
 * page. Le serveur garde la recherche ouverte pour servir les pages suivantes
 * (voir {@link fetchSearchPage}) ; la fermer avec {@link closeSearch}.
 *
 * @param query - Mots-clés de la recherche.
 * @param max - Nombre maximal de résultats au total, ou `null` pour aucune limite.
 * @param limit - Taille de la première page.
 * @returns L'identifiant de la recherche et sa première page.
 * @throws {Error} Si la requête échoue ou si le serveur répond une erreur.
 */
export async function startSearch(
	query: string,
	max: number | null,
	limit: number,
): Promise<SearchStart> {
	let response: Response;
	try {
		response = await fetch('/api/search', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ query, limit, max: max ?? undefined }),
		});
	} catch (error) {
		throw new Error('Impossible de contacter le serveur.', { cause: error });
	}

	if (!response.ok) {
		throw new Error(
			await readErrorMessage(response, `Échec de la recherche (${response.status}).`),
		);
	}

	try {
		return (await response.json()) as SearchStart;
	} catch (error) {
		throw new Error('Réponse du serveur invalide.', { cause: error });
	}
}

/**
 * Lit une page d'une recherche ouverte, via `GET /api/search/:id`.
 *
 * @param id - Identifiant retourné par {@link startSearch}.
 * @param offset - Position du premier résultat de la page (le `nextOffset` précédent).
 * @param limit - Nombre de résultats demandés.
 * @returns La page et la position de la suivante.
 * @throws {Error} Si la requête échoue, ou si la recherche a expiré côté serveur.
 */
export async function fetchSearchPage(
	id: string,
	offset: number,
	limit: number,
): Promise<VideoPage> {
	const params = new URLSearchParams({ offset: String(offset), limit: String(limit) });

	let response: Response;
	try {
		response = await fetch(`/api/search/${encodeURIComponent(id)}?${params.toString()}`);
	} catch (error) {
		throw new Error('Impossible de contacter le serveur.', { cause: error });
	}

	if (!response.ok) {
		throw new Error(
			await readErrorMessage(response, `Échec du chargement des résultats (${response.status}).`),
		);
	}

	try {
		return (await response.json()) as VideoPage;
	} catch (error) {
		throw new Error('Réponse du serveur invalide.', { cause: error });
	}
}

/**
 * Ferme une recherche côté serveur (`DELETE /api/search/:id`), ce qui libère
 * son processus yt-dlp. Une recherche déjà fermée ou expirée (`404`) n'est
 * pas une erreur.
 *
 * @param id - Identifiant de la recherche à fermer.
 * @throws {Error} Si le serveur est injoignable ou répond une erreur autre que « introuvable ».
 */
export async function closeSearch(id: string): Promise<void> {
	let response: Response;
	try {
		response = await fetch(`/api/search/${encodeURIComponent(id)}`, {
			method: 'DELETE',
			keepalive: true,
		});
	} catch (error) {
		throw new Error('Impossible de contacter le serveur.', { cause: error });
	}

	if (!response.ok && response.status !== 404) {
		throw new Error(
			await readErrorMessage(response, `Échec de la fermeture (${response.status}).`),
		);
	}
}
